<template>
  <div class="mr-emails-settings">
    <div class="mr-emails-settings-header">
      <div class="label">Material Request Emails</div>
    </div>
    <div>
      Here you can set the email addresses for Material Requests. Enter multiple addresses separated by ";", e.g.
      "address1@email.com;address2@email.com"
    </div>

    <div class="emails-group mt-4">
      <div class="label label-bold">MaterialRequest</div>

      <div class="pl-3 pr-3">
        <div class="label label-bold-black">Reply to Address</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> E-Mails sent from a material request will receive replies to this address.</div>
        <input
          @input="isDirty = true"
          type="text"
          class="form-control"
          v-model="entity.materialRequestReplyTo"
          placeholder="Reply to Address"
        />
      </div>

      <div class="mt-3 pl-3 pr-3">
        <div class="label label-bold-black">Request copy to</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> All vendor requests will be sent in copy to this addresses</div>
        <input @input="isDirty = true" type="text" class="form-control" v-model="entity.materialRequestCc" placeholder="Request copy to" />
      </div>

      <div class="mt-3 pl-3 pr-3">
        <div class="label label-bold-black">Blind copy to</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> All vendor requests will be sent in blind copy to this addresses</div>
        <input @input="isDirty = true" type="text" class="form-control" v-model="entity.materialRequestBcc" placeholder="Blind copy to" />
      </div>
    </div>

    <hr />

    <div class="emails-group mt-4">
      <div class="label label-bold">AogDesk Notification</div>

      <div class="pl-3 pr-3">
        <div class="label label-bold-black">Notification To:</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> All change notifications to a material request will be sent to this address</div>
        <input
          @input="isDirty = true"
          type="text"
          class="form-control"
          v-model="entity.materialRequestAogDeskNotificationEmailTo"
          placeholder="Notification To:"
        />
      </div>

      <div class="mt-3 pl-3 pr-3">
        <div class="label label-bold-black">Copy To:</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> All change notifications to a material request will be sent to this address in copy</div>
        <input
          @input="isDirty = true"
          type="text"
          class="form-control"
          v-model="entity.materialRequestAogDeskNotificationEmailCc"
          placeholder="Copy To:"
        />
      </div>
    </div>

    <hr />

    <div class="emails-group mt-4">
      <div class="label label-bold">Management Report</div>

      <div class="pl-3 pr-3 mb-3">
        <div class="label label-bold-black">Automatic Management Report E-Mail Interval in Minutes</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> The management report will be sent automatically for open material requests in the configured interval.</div>
        <input
          @input="isDirty = true"
          type="number"
          class="form-control"
          v-model="entity.autoManagementReportIntervalMinutes"
          placeholder="Report Interval (in minutes)"
        />
      </div>

      <div class="pl-3 pr-3">
        <div class="label label-bold-black">Management Report To</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> The management report will be sent to these addresses</div>
        <input
          @input="isDirty = true"
          type="text"
          class="form-control"
          v-model="entity.materialRequestManagementReportTo"
          placeholder="Management Report To"
        />
      </div>

      <div class="mt-3 pl-3 pr-3">
        <div class="label label-bold-black">Management Report Copy</div>
        <div class="mb-2"><i class="far fa-info-circle"></i> The management report will be sent to these addresses in copy</div>
        <input
          @input="isDirty = true"
          type="text"
          class="form-control"
          v-model="entity.materialRequestManagementReportCc"
          placeholder="Management Report Copy"
        />
      </div>
    </div>

    <hr />

    <div class="d-flex justify-content-end mt-4">
      <button :disabled="!isDirty" class="btn btn-save btn-sm btn-success" @click="save()">
        <i class="far fa-save"></i>
        Save changes
      </button>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { LabelModule, LabelStore } from '@/store/label';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import { IServiceResultOfSystemSettingsDto, LabelDto } from '@/apiclient/apiclient_generated';
  import { GlobalHelper } from '@/helpers/global-helper';
  import PublicConstants from '@/types/publicConstants';
  import VSwatches from 'vue-swatches';
  import OpsBoardIcons from '@/store/labelIcons';
  import RouteNames from '@/types/routeNames';
  import LabelsTable from './LabelsTable.vue';
  import { materialRequestClient } from '@/apiclient/opsboardapi';
  import MaterialRequestService from '@/services/materialRequest.service';
  import { UserModule, UserStore } from '@/store/user';

  @Component({ components: { VSwatches, LabelsTable } })
  export default class MaterialRequestEmails extends Vue {
    private userStore: UserModule = UserStore;
    private labelStore: LabelModule = LabelStore;
    private manuallyAddedLabels: LabelDto[] = [];
    isLoading = false;
    isDirty = false;
    swatches = PublicConstants.labelColors;

    mrEmailsDto = {
      entity: {
        materialRequestCc: '',
        materialRequestBcc: '',
        materialRequestManagementReportTo: '',
        materialRequestManagementReportCc: '',
        materialRequestLogisticsEmailCc: '',
        materialRequestReplyTo: '',
        materialRequestAogDeskNotificationEmailTo: '',
        materialRequestAogDeskNotificationEmailCc: '',
        autoManagementReportIntervalMinutes: 0
      },
    } as IServiceResultOfSystemSettingsDto;

    async mounted() {
      if (!MaterialRequestService.isMaterialRequestEmailsAvailable(this.userStore.user)) {
        return await this.$router.push({ name: RouteNames.boards });
      }

      this.mrEmailsDto = await materialRequestClient.getMaterialRequestSettings();
    }

    destroyed() {
      this.mrEmailsDto = { entity: {} } as IServiceResultOfSystemSettingsDto;
    }

    get entity() {
      return this.mrEmailsDto?.entity;
    }

    async save() {
      this.isLoading = true;
      const result = await materialRequestClient.updateMaterialRequestSettings(this.entity);
      this.isLoading = false;

      if (!result.isSuccess) {
        result.errors.forEach((error) => {
          GlobalHelper.NotifyError(error);
        });
      } else {
        GlobalHelper.NotifySuccess('Material Request Emails were successfully updated');
        this.mrEmailsDto = result;
      }
    }
  }
</script>

<style scoped lang="scss">
  .mr-emails-settings {
    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 10px;
    }

    &-container {
      border-radius: 4px;
      background-color: white;
      overflow-y: scroll;
      min-height: 300px;
      max-height: 600px;
      border: solid 1px #eaeaea;
      padding: 10px 20px 100px 10px;
    }

    .label-bold {
      font-weight: bold;
    }

    .label-bold-black {
      font-weight: bold;
      color: black !important;
    }
  }
</style>
